import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HiOutlineLockClosed, HiOutlineTable } from "react-icons/hi";
import { HiOutlineChartBar } from "react-icons/hi2";

export type ReportType =
  | "criteria"
  | "lifeCycle"
  | "performanceIndicators"
  | "strategy";

interface ReportTab {
  id: ReportType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  locked?: boolean;
}

interface ReportTabsProps {
  activeTab: ReportType;
  onTabChange: (tab: ReportType) => void;
  isNormalLicense?: boolean;
}

export default function ReportTabs({
  activeTab,
  onTabChange,
  isNormalLicense,
}: ReportTabsProps) {
  const { t } = useTranslation();

  const reportTabs: ReportTab[] = [
    {
      id: "criteria",
      label: "assessment.report.tabs.criteria",
      icon: HiOutlineTable,
    },
    {
      id: "lifeCycle",
      label: "assessment.report.tabs.lifeCycle",
      icon: HiOutlineChartBar,
    },
    {
      id: "performanceIndicators",
      label: "assessment.report.tabs.performanceIndicators",
      icon: HiOutlineChartBar,
      locked: isNormalLicense,
    },
    {
      id: "strategy",
      label: "assessment.report.tabs.strategy",
      icon: HiOutlineChartBar,
      locked: isNormalLicense,
    },
  ];

  return (
    <div className="relative flex flex-wrap gap-2 rounded-xl bg-brand-300 p-2 dark:bg-navy-700 md:gap-4 md:p-3">
      {reportTabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`
            relative flex flex-1 items-center justify-center gap-2 whitespace-nowrap 
            rounded-lg px-4 py-3 text-sm font-bold transition-all duration-300
            ${
              isActive
                ? "bg-white text-brand-600 shadow-lg ring-2 ring-brand-200 dark:bg-navy-500 dark:text-white dark:ring-navy-400"
                : "bg-white/20 text-white backdrop-blur-sm hover:bg-white/40 hover:text-white dark:bg-navy-600/50 dark:text-gray-300 dark:hover:bg-navy-600/70 dark:hover:text-white"
            }
            ${!isActive && "cursor-pointer hover:scale-[1.02] hover:shadow-md"}
          `}
          >
            <Icon
              className={`inline-block h-4 w-4 ${
                isActive ? "text-brand-500 dark:text-white" : ""
              }`}
            />
            <span>{t(tab.label)}</span>

            {tab.locked && (
              <HiOutlineLockClosed className="ml-2 h-4 w-4 text-brand-500" />
            )}

            {isActive && (
              <motion.span
                layoutId="activeTabIndicator"
                className="absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-brand-500 dark:bg-white"
                initial={false}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
